import { useState, useEffect, useMemo } from 'react';
import { TransactionWithCategory } from '@/types/transaction';
import { Account } from '@/types/account';
import { startOfMonth, endOfMonth } from 'date-fns';
import { parseTransactionDateUTC, calculateInvoiceDueDate } from '@/lib/utils';

interface UseDashboardDataProps {
  transactions: TransactionWithCategory[];
  accounts: Account[];
  selectedDate: Date;
}

export function useDashboardData({ transactions, accounts, selectedDate }: UseDashboardDataProps) {
  const [isLoading, setIsLoading] = useState(true);
  
  // Função para formatar valores monetários - memoizada
  const formatCurrency = useMemo(() => {
    const formatter = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
    
    return (value: number) => formatter.format(value);
  }, []);

  // Funções para manipulação de datas - memoizadas
  const dateFunctions = useMemo(() => {
    const parseTransactionDate = (transaction: TransactionWithCategory): Date => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tx = transaction as any;
      if (tx.type === 'CREDIT_CARD' && tx.creditCardInfo) {
        const { statementDate, dueDay } = tx.creditCardInfo;
        const parsedStatementDate = parseTransactionDateUTC(statementDate);
        return calculateInvoiceDueDate(parsedStatementDate, dueDay);
      }
      
      return parseTransactionDateUTC(transaction.data);
    };

    return {
      parseTransactionDateUTC,
      calculateInvoiceDueDate,
      parseTransactionDate
    };
  }, []);

  // Filtragem de transações - otimizada para evitar recálculos
  const filteredTransactions = useMemo(() => {
    const startDate = startOfMonth(selectedDate);
    const endDate = endOfMonth(selectedDate);
    
    return transactions.filter(transaction => {
      const transactionDate = dateFunctions.parseTransactionDate(transaction);
      // Usar >= e <= para incluir as bordas do mês
      return transactionDate >= startDate && transactionDate <= endDate;
    });
  }, [selectedDate, transactions, dateFunctions]);

  // Separar transações por tipo
  const expenseTransactions = useMemo(() => {
    return filteredTransactions.filter(t => t.valor < 0);
  }, [filteredTransactions]);

  const incomeTransactions = useMemo(() => {
    return filteredTransactions.filter(t => t.valor > 0);
  }, [filteredTransactions]);

  // Simulando um carregamento assíncrono para dados pesados
  useEffect(() => {
    setIsLoading(true);
    
    // Simular um pequeno atraso para permitir que a UI renderize primeiro
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [selectedDate, transactions]);

  return {
    isLoading,
    filteredTransactions,
    expenseTransactions,
    incomeTransactions,
    formatCurrency,
    dateFunctions
  };
}